import React, { useEffect, useState } from "react";
import api from "../api";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";

const initialFormData = {
  name: "",
  article_number: "", 
  hsn: "",
  mrp: "",
  price: "",
  wholesale_price: "",
  description: "",
  specification: "",
  manufacturing_details: "",
  category_id: "",
  subcategory_id: "",
  product_type: "none",
  qty: "",
  gst: "",
  video_link: "",
  return_days: "",
};

const initialImages = {
  image: null,
  image2: null,
  image3: null,
  image4: null,
};

const AddProduct = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [images, setImages] = useState(initialImages);
  const [previews, setPreviews] = useState(initialImages);

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");

  const [openSections, setOpenSections] = useState({
    basic: true,
    details: true,
    category: true,
    colors: true,
    images: true,
  });

  // ==========================================
  // AUTH HEADERS
  // ==========================================
  const getAuthHeaders = () => {
    const token =
      localStorage.getItem("token");

    return token
      ? { Authorization: `Bearer ${token}` }
      : {};
  };

  // ==========================================
  // FETCH CATEGORIES
  // ==========================================
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get("categories", {
          headers: getAuthHeaders(),
        });

        if (res.data.status === false) {
          throw new Error(res.data.message || "Failed to load categories");
        }

        const data = res.data.data;
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Categories error:", error);
        toast.error(
          error.response?.data?.message || "Failed to load categories"
        );
      }
    };

    fetchCategories();
  }, []);

  // ==========================================
  // FETCH AND FILTER SUBCATEGORIES
  // ==========================================
  useEffect(() => {
    if (!formData.category_id) {
      setSubcategories([]);
      return;
    }

    const fetchSubcategories = async () => {
      try {
        const res = await api.get("subcategories", {
          headers: getAuthHeaders(),
        });

        const data = Array.isArray(res.data.data) ? res.data.data : [];

        setSubcategories(
          data.filter(
            (item) =>
              String(item.category_id) === String(formData.category_id)
          )
        );
      } catch (error) {
        console.error("Subcategories error:", error);
        setSubcategories([]);
        toast.error("Failed to load subcategories");
      }
    };

    fetchSubcategories();
  }, [formData.category_id]);

  // ==========================================
  // BASIC INPUT CHANGE
  // ==========================================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "category_id" ? { subcategory_id: "" } : {}),
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
      ...(name === "category_id" ? { subcategory_id: undefined } : {}),
    }));
  };

  const handleRichTextChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  };

  // ==========================================
  // MAIN PRODUCT IMAGE UPLOAD
  // ==========================================
  const handleImageChange = (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image");
      e.target.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Each image must be 10 MB or smaller");
      e.target.value = "";
      return;
    }

    setImages((prev) => ({
      ...prev,
      [field]: file,
    }));

    setPreviews((prev) => ({
      ...prev,
      [field]: URL.createObjectURL(file),
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  };

  // ==========================================
  // COLOR HANDLERS
  // ==========================================
  const addColor = () => {
    setColors((prev) => [
      ...prev,
      {
        name: "",
        code: "#000000",
        images: [],
        sizes: [{ size: "", qty: "" }],
      },
    ]);
  };

  const removeColor = (colorIndex) => {
    setColors((prev) => prev.filter((_, index) => index !== colorIndex));
  };

  const handleColorChange = (index, field, value) => {
    setColors((prev) =>
      prev.map((color, i) =>
        i === index ? { ...color, [field]: value } : color
      )
    );
  };

  const handleColorImageChange = (e, colorIndex) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    const invalidFile = files.find(
      (file) =>
        !file.type.startsWith("image/") ||
        file.size > 10 * 1024 * 1024
    );

    if (invalidFile) {
      toast.error("Select valid images of 10 MB or less each");
      e.target.value = "";
      return;
    }

    setColors((prev) =>
      prev.map((color, index) =>
        index === colorIndex
          ? { ...color, images: [...color.images, ...files] }
          : color
      )
    );

    e.target.value = "";
  };

  const removeColorImage = (colorIndex, imageIndex) => {
    setColors((prev) =>
      prev.map((color, index) =>
        index === colorIndex
          ? {
              ...color,
              images: color.images.filter((_, i) => i !== imageIndex),
            }
          : color
      )
    );
  };

  const addColorSize = (colorIndex) => {
    setColors((prev) =>
      prev.map((color, index) =>
        index === colorIndex
          ? {
              ...color,
              sizes: [...color.sizes, { size: "", qty: "" }],
            }
          : color
      )
    );
  };

  const removeColorSize = (colorIndex, sizeIndex) => {
    setColors((prev) =>
      prev.map((color, index) =>
        index === colorIndex
          ? {
              ...color,
              sizes: color.sizes.filter((_, i) => i !== sizeIndex),
            }
          : color
      )
    );
  };

  const handleColorSizeChange = (
    colorIndex,
    sizeIndex,
    field,
    value
  ) => {
    setColors((prev) =>
      prev.map((color, index) =>
        index === colorIndex
          ? {
              ...color,
              sizes: color.sizes.map((size, i) =>
                i === sizeIndex ? { ...size, [field]: value } : size
              ),
            }
          : color
      )
    );
  };

  // ==========================================
  // RESET FORM
  // ==========================================
  const resetForm = () => {
    Object.values(previews).forEach((url) => {
      if (url && url.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
    });

    setFormData(initialFormData);
    setImages(initialImages);
    setPreviews(initialImages);
    setColors([]);
    setErrors({});
    setGeneralError("");

    document.querySelectorAll('input[type="file"]').forEach((input) => {
      input.value = "";
    });
  };

  // ==========================================
  // SUBMIT PRODUCT
  // ==========================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    setLoading(true);
    setGeneralError("");
    setErrors({});

    const fd = new FormData();

    // Append all non-empty basic fields.
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        fd.append(key, value);
      }
    });

    // Append main images.
    Object.entries(images).forEach(([key, file]) => {
      if (file) fd.append(key, file);
    });

    // Append nested colors, images and sizes.
    colors.forEach((color, colorIndex) => {
      fd.append(`colors[${colorIndex}][name]`, color.name.trim());

      if (color.code) {
        fd.append(`colors[${colorIndex}][code]`, color.code);
      }

      color.images.forEach((file, imageIndex) => {
        fd.append(
          `colors[${colorIndex}][images][${imageIndex}]`,
          file
        );
      });

      color.sizes.forEach((size, sizeIndex) => {
        fd.append(
          `colors[${colorIndex}][sizes][${sizeIndex}][size]`,
          size.size.trim()
        );

        fd.append(
          `colors[${colorIndex}][sizes][${sizeIndex}][qty]`,
          size.qty === "" ? "0" : size.qty
        );
      });
    });

    try {
      const res = await api.post("add-product", fd, {
        headers: {
          ...getAuthHeaders(),
          // Let Axios/browser set the multipart boundary automatically.
        },
      });

      if (res.data.status === false) {
        throw new Error(res.data.message || "Failed to add product");
      }

      toast.success(res.data.message || "Product added successfully");
      resetForm();
    } catch (error) {
      console.error("Add product error:", error);

      const responseData = error.response?.data;

      if (error.response?.status === 422) {
        const validationErrors = responseData?.errors || {};

        setErrors(validationErrors);

        const firstError = Object.values(validationErrors)
          .flat()
          .find(Boolean);

        setGeneralError(
          firstError ||
            responseData?.message ||
            "Please check the entered product details"
        );

        toast.error("Please check the highlighted validation errors");
      } else {
        const message =
          responseData?.message ||
          error.message ||
          "Something went wrong. Please try again.";

        setGeneralError(message);
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // UI HELPERS
  // ==========================================
  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const sectionClass =
    "rounded-2xl border border-[#594719]/70 bg-[#17130d] overflow-hidden";

  const sectionHeaderClass =
    "flex w-full items-center justify-between gap-3 px-5 py-4 text-left bg-gradient-to-r from-[#29200e] to-[#1d170c] hover:from-[#332710] transition";

  const inputClass =
    "w-full min-w-0 rounded-xl border border-[#57461c] bg-[#0d0b08] px-4 py-3 text-sm text-[#eee1ad] placeholder:text-[#716649] outline-none transition focus:border-[#d4af37] focus:ring-2 focus:ring-[#d4af37]/15";

  const labelClass =
    "mb-2 block text-sm font-semibold text-[#d5c69b]";

  const goldButtonClass =
    "inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-[#e4c653] to-[#a77c0b] px-5 py-3 text-sm font-bold text-[#17130d] shadow-lg shadow-[#d4af37]/10 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <div className="min-h-screen bg-black/90 px-3 py-5 sm:px-5 sm:py-8 lg:px-8">
      <div className="mx-auto max-w-6xl">
        {/* PAGE HEADER */}
        <div className="relative mb-6 overflow-hidden rounded-2xl border border-[#8b6914]/70 bg-gradient-to-br from-[#30240e] via-[#17130d] to-[#0b0906] p-5 shadow-2xl sm:mb-8 sm:rounded-3xl sm:p-8">
          <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-[#d4af37]/10 blur-3xl" />

          <div className="relative flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#f5dc78] to-[#a77c0b] text-xl font-black text-[#17130d] shadow-lg sm:h-14 sm:w-14 sm:text-2xl">
              +
            </div>

            <div>
              <h1 className="text-2xl font-bold tracking-tight text-[#e3c65b] sm:text-3xl lg:text-4xl">
                Add New Product
              </h1>
              <p className="mt-1 text-sm text-[#b7a575] sm:text-base">
                Add product details, inventory, colors and images
              </p>
            </div>
          </div>
        </div>

        {/* MAIN FORM CARD */}
        <div className="overflow-hidden rounded-2xl border border-[#66521e]/70 bg-[#100e0a] shadow-2xl sm:rounded-3xl">
          <form onSubmit={handleSubmit} className="space-y-5 p-4 sm:space-y-6 sm:p-6 lg:p-8">
            {generalError && (
              <div className="rounded-xl border border-red-500/40 bg-red-950/40 p-4 text-sm text-red-300">
                {generalError}
              </div>
            )}

            {/* BASIC INFORMATION */}
            <section className={sectionClass}>
              <button
                type="button"
                onClick={() => toggleSection("basic")}
                className={sectionHeaderClass}
              >
                <div>
                  <h2 className="font-bold text-[#e3c65b]">
                    01. Basic Product Information
                  </h2>
                  <p className="mt-1 text-xs text-[#96845a]">
                    Name, article number, HSN and prices
                  </p>
                </div>
                <span className="text-xl text-[#d4af37]">
                  {openSections.basic ? "−" : "+"}
                </span>
              </button>

              {openSections.basic && (
                <div className="grid grid-cols-1 gap-5 p-4 sm:grid-cols-2 sm:p-5 lg:grid-cols-3">
                  <TextField label="Product Name *" name="name" value={formData.name} onChange={handleChange} error={errors.name} required inputClass={inputClass} labelClass={labelClass} />
                  <TextField label="Article Number *" name="article_number" value={formData.article_number} onChange={handleChange} error={errors.article_number} required inputClass={inputClass} labelClass={labelClass} />
                  <TextField label="HSN Code" name="hsn" value={formData.hsn} onChange={handleChange} error={errors.hsn} inputClass={inputClass} labelClass={labelClass} />
                  <TextField label="MRP Price" name="mrp" type="number" value={formData.mrp} onChange={handleChange} error={errors.mrp_price || errors.mrp} inputClass={inputClass} labelClass={labelClass} />
                  <TextField label="Selling Price *" name="price" type="number" value={formData.price} onChange={handleChange} error={errors.price} required inputClass={inputClass} labelClass={labelClass} />
                  <TextField label="Wholesale Price" name="wholesale_price" type="number" value={formData.wholesale_price} onChange={handleChange} error={errors.wholesale_price} inputClass={inputClass} labelClass={labelClass} />
                </div>
              )}
            </section>

            {/* PRODUCT DETAILS */}
            <section className={sectionClass}>
              <button
                type="button"
                onClick={() => toggleSection("details")}
                className={sectionHeaderClass}
              >
                <div>
                  <h2 className="font-bold text-[#e3c65b]">
                    02. Product Details & Tax
                  </h2>
                  <p className="mt-1 text-xs text-[#96845a]">
                    Description, specifications, inventory and GST
                  </p>
                </div>
                <span className="text-xl text-[#d4af37]">
                  {openSections.details ? "−" : "+"}
                </span>
              </button>

              {openSections.details && (
                <div className="space-y-6 p-4 sm:p-5">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    <TextField label="Quantity" name="qty" type="number" value={formData.qty} onChange={handleChange} error={errors.qty} inputClass={inputClass} labelClass={labelClass} />
                    <TextField label="GST (%)" name="gst" type="number" value={formData.gst} onChange={handleChange} error={errors.gst} inputClass={inputClass} labelClass={labelClass} />
                    <TextField label="Return Days" name="return_days" type="number" value={formData.return_days} onChange={handleChange} error={errors.return_days} inputClass={inputClass} labelClass={labelClass} />
                    <TextField label="Video Link" name="video_link" type="url" value={formData.video_link} onChange={handleChange} error={errors.video_link} inputClass={inputClass} labelClass={labelClass} />
                  </div>

                  <RichTextField
                    label="Description"
                    value={formData.description}
                    onChange={(value) => handleRichTextChange("description", value)}
                    error={errors.description}
                  />

                  <RichTextField
                    label="Specification"
                    value={formData.specification}
                    onChange={(value) => handleRichTextChange("specification", value)}
                    error={errors.specification}
                  />

                  <RichTextField
                    label="Manufacturing Details"
                    value={formData.manufacturing_details}
                    onChange={(value) => handleRichTextChange("manufacturing_details", value)}
                    error={errors.manufacturing_details}
                  />
                </div>
              )}
            </section>

            {/* CATEGORY */}
            <section className={sectionClass}>
              <button
                type="button"
                onClick={() => toggleSection("category")}
                className={sectionHeaderClass}
              >
                <div>
                  <h2 className="font-bold text-[#e3c65b]">
                    03. Category & Product Type
                  </h2>
                  <p className="mt-1 text-xs text-[#96845a]">
                    Choose the product category and promotion type
                  </p>
                </div>
                <span className="text-xl text-[#d4af37]">
                  {openSections.category ? "−" : "+"}
                </span>
              </button>

              {openSections.category && (
                <div className="grid grid-cols-1 gap-5 p-4 sm:grid-cols-2 sm:p-5 lg:grid-cols-3">
                  <SelectField
                    label="Category *"
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                    list={categories}
                    error={errors.category_id}
                    required
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />

                  <SelectField
                    label="Subcategory"
                    name="subcategory_id"
                    value={formData.subcategory_id}
                    onChange={handleChange}
                    list={subcategories}
                    error={errors.subcategory_id}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />

                  <div>
                    <label className={labelClass}>Product Type</label>
                    <select
                      name="product_type"
                      value={formData.product_type}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="none">None</option>
                      <option value="new">New Arrival</option>
                      <option value="special_offer">Special Offer</option>
                      <option value="hot_deal">Hot Deal</option>
                    </select>
                    <FieldError error={errors.product_type} />
                  </div>
                </div>
              )}
            </section>

            {/* COLORS AND SIZES */}
            <section className={sectionClass}>
              <button
                type="button"
                onClick={() => toggleSection("colors")}
                className={sectionHeaderClass}
              >
                <div>
                  <h2 className="font-bold text-[#e3c65b]">
                    04. Colors, Variants & Sizes
                  </h2>
                  <p className="mt-1 text-xs text-[#96845a]">
                    Configure separate images and stock for each color
                  </p>
                </div>
                <span className="text-xl text-[#d4af37]">
                  {openSections.colors ? "−" : "+"}
                </span>
              </button>

              {openSections.colors && (
                <div className="space-y-5 p-4 sm:p-5">
                  {colors.length === 0 && (
                    <div className="rounded-xl border border-dashed border-[#594719] bg-[#0e0c08] px-4 py-8 text-center">
                      <p className="font-medium text-[#cbbb8a]">
                        No color variants added
                      </p>
                      <p className="mt-1 text-sm text-[#827451]">
                        Add colors if this product has multiple variants.
                      </p>
                    </div>
                  )}

                  {colors.map((color, index) => (
                    <div
                      key={index}
                      className="rounded-2xl border border-[#66521e]/70 bg-[#0e0c08] p-4 sm:p-5"
                    >
                      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                        <h3 className="font-bold text-[#e3c65b]">
                          Color Variant {index + 1}
                        </h3>
                        <button
                          type="button"
                          onClick={() => removeColor(index)}
                          className="rounded-lg border border-red-900/70 px-3 py-2 text-xs font-semibold text-red-300 hover:bg-red-950/40"
                        >
                          Remove Color
                        </button>
                      </div>

                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <div>
                          <label className={labelClass}>Color Name *</label>
                          <input
                            type="text"
                            value={color.name}
                            onChange={(e) =>
                              handleColorChange(index, "name", e.target.value)
                            }
                            placeholder="e.g. Black"
                            className={inputClass}
                            required
                          />
                          <FieldError error={errors[`colors.${index}.name`]} />
                        </div>

                        <div>
                          <label className={labelClass}>Color Code</label>
                          <div className="flex min-h-[48px] items-center gap-3 rounded-xl border border-[#57461c] bg-[#0d0b08] p-2">
                            <input
                              type="color"
                              value={color.code || "#000000"}
                              onChange={(e) =>
                                handleColorChange(index, "code", e.target.value)
                              }
                              className="h-9 w-12 cursor-pointer rounded border-0 bg-transparent"
                              aria-label="Choose color"
                            />
                            <span className="font-mono text-sm text-[#d4c69a]">
                              {color.code || "#000000"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* COLOR IMAGES */}
                      <div className="mt-6">
                        <label className={labelClass}>Color Images</label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => handleColorImageChange(e, index)}
                          className="block w-full text-sm text-[#b6a77e] file:mr-4 file:rounded-lg file:border-0 file:bg-[#3b2e10] file:px-4 file:py-2.5 file:font-semibold file:text-[#e3c65b] hover:file:bg-[#4a3912]"
                        />

                        {color.images.length > 0 && (
                          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                            {color.images.map((file, imageIndex) => (
                              <div
                                key={`${file.name}-${imageIndex}`}
                                className="relative overflow-hidden rounded-xl border border-[#594719]"
                              >
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={`${color.name || "Color"} ${imageIndex + 1}`}
                                  className="h-28 w-full object-cover"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeColorImage(index, imageIndex)}
                                  className="absolute right-2 top-2 rounded-lg bg-black/80 px-2 py-1 text-xs text-white"
                                  aria-label="Remove color image"
                                >
                                  ×
                                </button>
                                <p className="truncate bg-[#17130d] px-2 py-1 text-xs text-[#cbbb8a]">
                                  {file.name}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* SIZES */}
                      <div className="mt-6 border-t border-[#493a19] pt-5">
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                          <h4 className="font-semibold text-[#d5c69b]">
                            Sizes & Stock
                          </h4>
                          <button
                            type="button"
                            onClick={() => addColorSize(index)}
                            className="text-sm font-semibold text-[#e3c65b] hover:underline"
                          >
                            + Add Size
                          </button>
                        </div>

                        {color.sizes.map((size, sizeIndex) => (
                          <div
                            key={sizeIndex}
                            className="mb-3 grid grid-cols-1 items-start gap-3 sm:grid-cols-[1fr_1fr_auto]"
                          >
                            <div>
                              <input
                                type="text"
                                value={size.size}
                                onChange={(e) =>
                                  handleColorSizeChange(
                                    index,
                                    sizeIndex,
                                    "size",
                                    e.target.value
                                  )
                                }
                                placeholder="Size (S, M, L, XL)"
                                className={inputClass}
                                required
                              />
                              <FieldError
                                error={
                                  errors[
                                    `colors.${index}.sizes.${sizeIndex}.size`
                                  ]
                                }
                              />
                            </div>

                            <div>
                              <input
                                type="number"
                                min="0"
                                value={size.qty}
                                onChange={(e) =>
                                  handleColorSizeChange(
                                    index,
                                    sizeIndex,
                                    "qty",
                                    e.target.value
                                  )
                                }
                                placeholder="Quantity"
                                className={inputClass}
                              />
                              <FieldError
                                error={
                                  errors[
                                    `colors.${index}.sizes.${sizeIndex}.qty`
                                  ]
                                }
                              />
                            </div>

                            <button
                              type="button"
                              onClick={() => removeColorSize(index, sizeIndex)}
                              disabled={color.sizes.length === 1}
                              className="rounded-xl border border-[#57461c] px-4 py-3 text-sm text-[#cbbb8a] hover:border-red-700 hover:text-red-300 disabled:opacity-30"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addColor}
                    className="w-full rounded-xl border border-dashed border-[#94711a] bg-[#21190b] px-4 py-4 font-semibold text-[#e3c65b] transition hover:bg-[#30240e]"
                  >
                    + Add Another Color
                  </button>
                </div>
              )}
            </section>

            {/* MAIN IMAGES */}
            <section className={sectionClass}>
              <button
                type="button"
                onClick={() => toggleSection("images")}
                className={sectionHeaderClass}
              >
                <div>
                  <h2 className="font-bold text-[#e3c65b]">
                    05. Main Product Images
                  </h2>
                  <p className="mt-1 text-xs text-[#96845a]">
                    Upload up to four main images
                  </p>
                </div>
                <span className="text-xl text-[#d4af37]">
                  {openSections.images ? "−" : "+"}
                </span>
              </button>

              {openSections.images && (
                <div className="grid grid-cols-1 gap-5 p-4 sm:grid-cols-2 sm:p-5 lg:grid-cols-4">
                  {["image", "image2", "image3", "image4"].map(
                    (field, index) => (
                      <ImageUpload
                        key={field}
                        label={`Image ${index + 1}`}
                        field={field}
                        preview={previews[field]}
                        onChange={handleImageChange}
                        error={errors[field]}
                        inputClass={inputClass}
                        labelClass={labelClass}
                      />
                    )
                  )}
                </div>
              )}
            </section>

            {/* SUBMIT BUTTONS */}
            <div className="flex flex-col-reverse gap-3 border-t border-[#493a19] pt-6 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={resetForm}
                disabled={loading}
                className="rounded-xl border border-[#594719] bg-[#17130d] px-6 py-3.5 text-sm font-semibold text-[#cbbb8a] transition hover:border-[#b89632] hover:text-[#e3c65b] disabled:opacity-50"
              >
                Clear Form
              </button>

              <button
                type="submit"
                disabled={loading}
                className={`${goldButtonClass} w-full py-4 text-base sm:w-auto sm:min-w-52`}
              >
                {loading ? "Adding Product..." : "Add Product"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// REUSABLE COMPONENTS
// ==========================================
const FieldError = ({ error }) => {
  if (!error) return null;

  const message = Array.isArray(error) ? error[0] : error;

  return (
    <p className="mt-1 text-xs text-red-400">
      {message}
    </p>
  );
};

const TextField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  error,
  placeholder,
  required = false,
  inputClass,
  labelClass,
}) => (
  <div>
    <label className={labelClass}>
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder || `Enter ${label.replace(" *", "").toLowerCase()}`}
      required={required}
      min={type === "number" ? 0 : undefined}
      step={type === "number" ? "any" : undefined}
      className={inputClass}
    />
    <FieldError error={error} />
  </div>
);

const SelectField = ({
  label,
  name,
  value,
  onChange,
  list,
  error,
  required = false,
  inputClass,
  labelClass,
}) => (
  <div>
    <label className={labelClass}>
      {label}
    </label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className={inputClass}
    >
      <option value="">Select {label.replace(" *", "")}</option>
      {list.map((item) => (
        <option key={item.id} value={item.id}>
          {item.name}
        </option>
      ))}
    </select>
    <FieldError error={error} />
  </div>
);

const RichTextField = ({ label, value, onChange, error }) => (
  <div>
    <label className="mb-2 block text-sm font-semibold text-[#d5c69b]">
      {label}
    </label>
    <div className="rounded-xl bg-white text-black [&_.ql-toolbar]:rounded-t-xl [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-gray-200 [&_.ql-container]:border-0 [&_.ql-container]:rounded-b-xl [&_.ql-editor]:min-h-[130px]">
      <ReactQuill
        theme="snow"
        value={value}
        onChange={onChange}
        placeholder={`Enter ${label.toLowerCase()}...`}
      />
    </div>
    <FieldError error={error} />
  </div>
);

const ImageUpload = ({
  label,
  field,
  preview,
  onChange,
  error,
  inputClass,
  labelClass,
}) => (
  <div className="min-w-0 rounded-xl border border-[#493a19] bg-[#0d0b08] p-3">
    <label className={labelClass}>
      {label}
    </label>
    <input
      type="file"
      accept="image/*"
      onChange={(e) => onChange(e, field)}
      className="block w-full min-w-0 text-xs text-[#b6a77e] file:mb-2 file:mr-2 file:rounded-lg file:border-0 file:bg-[#3b2e10] file:px-3 file:py-2 file:font-semibold file:text-[#e3c65b] hover:file:bg-[#4a3912]"
    />

    {preview ? (
      <div className="mt-3">
        <img
          src={preview}
          alt={`${label} preview`}
          className="h-36 w-full rounded-lg border border-[#594719] object-cover"
        />
        <p className="mt-2 truncate text-xs text-[#a99a70]">
          Preview selected
        </p>
      </div>
    ) : (
      <div className="mt-3 flex h-36 items-center justify-center rounded-lg border border-dashed border-[#493a19] bg-[#100e0a] text-xs text-[#716649]">
        No image selected
      </div>
    )}

    <FieldError error={error} />
  </div>
);

export default AddProduct;
